<template>
  <main class="login" :login-state="loginState" v-if="school.school_id">
    <section>
      <div class="branding">
        <figure class="logo" alt="PTC Wizard Logo">
          <Logo />
        </figure>
        <div class="school-name">{{ schoolStore.school.school_name }}</div>
        <div class="dropdown">
          <button class="btn text-black dropdown-toggle text-capitalize fs-normal px-2"
          type="button"
          id="dropdownLanguageButton"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          >
          <component :is="currentLanguageFlag"></component>
          {{ currentLanguage.code.toUpperCase() }}
          </button>
          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownLanguageButton">
            <li v-for="language in supportedLanguages" :key="language.code">
              <span 
                class="dropdown-item" 
                @click="changeLocale(language.code)" 
                :class="{ active: $i18n.locale === language.code }"
              >
                <component :is="getFlagComponent(language.flag)"></component>
                &nbsp; {{ language.nativeName }}
              </span>
            </li>
          </ul>
          </div>
      </div>
      <div class="card">
        <form
          class="needs-validation"
          v-if="!showForgotPass"
          :class="{ 'was-validated': toggleValidation, failed: toggleFail }"
          novalidate=""
          @submit.prevent="login"
        >
          <h4 class="card-header" v-t="'message.Login.title'"></h4>
          <div class="card-body">
            <div class="form-floating" field="username">
              <input
                type="text"
                class="form-control form-control-lg"
                id="floatingInput"
                :placeholder="placeholderText('username')"
                v-model="username"
                required
                autocomplete="username"
                data-cy="login-username"
              />
              <label for="floatingInput">{{ placeholderText("username") }}</label>
              <!-- <div class="invalid-tooltip">Username is required</div> -->
            </div>
            <div class="form-floating" field="password">
              <input
                type="password"
                class="form-control form-control-lg"
                id="floatingPassword"
                :placeholder="placeholderText('password')"
                v-model="password"
                required
                autocomplete="current-password"
                data-cy="login-password"
              />
              <label for="floatingPassword">{{ placeholderText("password") }}</label>
              <!-- <div class="invalid-tooltip">Password is required</div> -->
            </div>
            <button
              class="submit"
              :disabled="toggleSubmit"
              data-cy="login-submit-btn"
              type="submit"
            >
              <i class="bi bi-lock opacity-50"></i>
              <span>{{ $t('message.Login.sign_in') }}</span>
            </button>
            <div class="alert alert-danger px-5 mt-2" v-if="toggleFail">
              <!-- Login failed for some reason. Please try again, or call the school at (444) 444-4444. -->
              {{ $t('message.Login.login_fail') }}
            </div>
            <div class="sso">
              <div class="caps"><span v-t="'message.Login.or_sign_in_with'"></span></div>
              <div class="hstack">
                <button class="btn" @click="signinWithGoogle" type="button">
                  <LogoGoogle />
                  <span>Google</span>
                </button>
                <button class="btn" @click="signinWithMicrosoft" type="button">
                  <LogoMicrosoft />
                  <span>Microsoft</span>
                </button>
                <!-- <button class="btn">
									<LogoClever />
									<span>Clever</span>
								</button>
								<button class="btn">
									<LogoGG4L />
									<span>GG4L</span>
								</button> -->
              </div>
            </div>
          </div>
          <div
            class="card-footer"
            v-if="school.integration == 'csv' || school.integration == 'none' || school.integration == 'hybrid' || school.integration == 'nycdoe' || school.integration == 'oneroster'"
          >
            <button
              class="btn btn-link"
              v-if="school.integration == 'csv' || school.integration == 'none' || school.integration == 'hybrid' || school.integration == 'nycdoe' || school.integration == 'oneroster'"
              @click="showForgotPass = true"
            >
              <i class="bi bi-arrow-right me-2 opacity-50"></i><span v-t="'message.Login.forgot_password'"></span>
            </button>
            <router-link
              :to="{
                name: 'parent-CreateAccount',
                params: { account: $route.params.account }
              }"
              class="btn btn-link"
              v-if="school.integration == 'none' || school.integration == 'hybrid' || school.integration == 'nycdoe'"
            >
              <i class="bi bi-person-plus me-2 opacity-50"></i><span v-t="'message.Login.create_account'"></span>
            </router-link>
            <!-- <button class="btn btn-link" v-if="school.integration == 'none'"><i class="bi bi-person-plus me-2 opacity-50"></i><span>Create Account</span></button> -->
          </div>
        </form>

        <form
          class="needs-validation"
          v-if="showForgotPass"
          @submit.prevent="passwordReminder"
          :class="{ 'was-validated': toggleValidation, failed: toggleFail }"
          novalidate=""
        >
          <h4 class="card-header">Password Reminder</h4>
          <div class="card-body" v-if="!passwordReminderSent">
            <div class="form-floating" field="password">
              <input
                type="text"
                class="form-control form-control-lg"
                id="passwordReminder"
                placeholder="Enter your email address"
                v-model="passwordReminderEmail"
                required
              />
              <label for="passwordReminder" v-t="'message.Login.enter_email'"></label>
              <div class="invalid-tooltip" v-t="'message.Login.validation_error'"></div>
            </div>
            <button class="submit" :disabled="toggleSubmit">
              <i class="bi bi-envelope opacity-50"></i>
              <span v-t="'message.Login.password_reminder'"></span>
            </button>
          </div>
          <div class="card-body" v-if="passwordReminderError">
            <p>
              <strong>{{ $t('message.Login.password_reminder_error_no_user_register', {email: passwordReminderEmail}) }}</strong>
            </p>
            
          </div>
          <div class="card-body" v-if="passwordReminderSent">
            <p>
              <strong>{{ $t('message.Login.password_reminder_sent', {email: passwordReminderEmail}) }}</strong>
            </p>
            <p v-t="'message.Login.password_reminder_sent_message'"></p>
            <p v-if="schoolStore.school.school_email">
              {{ $t('message.Login.password_trouble_email', {email: schoolStore.school.school_email}) }}
            </p>
            <p v-else v-t="'message.Login.password_trouble_school'"></p>
          </div>
          <div class="card-footer">
            <button
              class="btn btn-link"
              @click="[(showForgotPass = false), (passwordReminderSent = false)]"
            >
              <i class="bi bi-arrow-left me-2 opacity-50"></i><span v-t="'message.Login.return_to_sign_in'"></span>
            </button>
          </div>
        </form>
      </div>
      <small class="text-muted vstack gap-1">
        <strong class="opacity-50">Parent Teacher Conference Scheduling Made Easy</strong>
        <div>&copy;2006 - {{ new Date().getFullYear() }} PTC Wizard, All rights reserved.</div>
      </small>
    </section>
  </main>
  <main v-else class="login loading-splash">
    <LoadingIcon />
    <span>Loading School Data…..</span>
  </main>
  
</template>

<script setup>
import AuthService from "@/services/auth.service";
import { useAuthenticationStore } from "@/stores/authentication";
import { useSchoolStore } from "@/stores/school";
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";

// Flag imports
import FlagUS from "@/assets/icons/flags/usa.svg";
import FlagES from "@/assets/icons/flags/es.svg";
import FlagFR from "@/assets/icons/flags/fr.svg";
import FlagDE from "@/assets/icons/flags/de.svg";
import FlagAR from "@/assets/icons/flags/ar.svg";
import FlagCN from "@/assets/icons/flags/cn.svg";
import FlagJP from "@/assets/icons/flags/jp.svg";
import FlagBD from "@/assets/icons/flags/bd.svg";
import FlagKR from "@/assets/icons/flags/kr.svg";
import FlagPK from "@/assets/icons/flags/pk.svg";
import FlagSN from "@/assets/icons/flags/sn.svg";

import Logo from "@/assets/icons/logo-vertical-regular.svg";
import LoadingIcon from "@/assets/icons/loading.svg";

import LogoGoogle from "@/assets/brand/google.svg";
import LogoMicrosoft from "@/assets/brand/microsoft.svg";
import { integrationWindow } from "@/utils/integrationWindow";
import FetchService from "@/services/fetch.service";
import { useLanguage } from "@/composables/useLanguage.js";

const router = useRouter();
const route = useRoute();
const authenticationStore = useAuthenticationStore();
const schoolStore = useSchoolStore();
const { currentLanguage, supportedLanguages, changeLocale } = useLanguage();

// Flag component mapping
const flagComponents = {
  'usa': FlagUS,
  'es': FlagES,
  'fr': FlagFR,
  'de': FlagDE,
  'ar': FlagAR,
  'cn': FlagCN,
  'jp': FlagJP,
  'bd': FlagBD,
  'kr': FlagKR,
  'pk': FlagPK,
  'sn': FlagSN
};

const currentLanguageFlag = computed(() => {
  return flagComponents[currentLanguage.value.flag] || FlagUS;
});

function getFlagComponent(flagKey) {
  return flagComponents[flagKey] || FlagUS;
}

const school = ref({});
// const route = useRoute()
//   const loading = ref(true);
//   const error = ref(null);
const username = ref(null);
const password = ref(null);
const passwordReminderEmail = ref(null);
const passwordReminderSent = ref(false);
const passwordReminderError = ref(false);
const passwordError = ref(false);
const showForgotPass = ref(false);
const googleUrl = ref(null);
function passwordReminder() {
  toggleSubmit.value = true;
  toggleValidation.value = true;
  var user = {
    email: passwordReminderEmail.value,
    account: route.params.account,
    authentication_type: "parent"
  };
  AuthService.passwordReminder(user).then((result) => {
    console.log(result);
    if (result.success) {
      toggleSubmit.value = false;
      passwordReminderSent.value = true;
      passwordError.value = false;
      toggleValidation.value = false;
    } else {
      // Handle error by displaying the message
      passwordError.value = result.message;
      passwordReminderError.value=true;
      toggleSubmit.value = false;
      passwordReminderSent.value = false;
    }
  });
}

function signinWithGoogle() {
  window.open(googleUrl.value);
}

async function signinWithMicrosoft() {
  const res = await integrationWindow(
    FetchService.getUrl(
      "*/oauth2/microsoft?school_id=" +
        schoolStore.school.school_id +
        "&type=parent&rand=" +
        Math.random() * 99999999999,
      undefined,
      "microsoft_sign_in"
    )
  );
  if (res.success && res.resData) {
    const token = AuthService.loginFromHash(res.resData.jwt);
    authenticationStore.setJWT(token);
    router.push("/" + route.params.account);
  }
}

function login() {
  toggleValidation.value = true;
  toggleSubmit.value = true;
  authenticationStore
    .login({
      username: username.value,
      password: password.value,
      account: school.value.subdomain,
      district_id: school.value.district_id
    })
    .then((response) => {
      if (response) {
        router.push("/" + route.params.account);
      } else {
        toggleFail.value = true;
        toggleSubmit.value = false;
      }
    });
}

function placeholderText(field) {
  if (school.value.integration == "infinitecampus" && field == "username") {
    return "Infinite Campus Username";
  } else if (school.value.integration != "infinitecampus" && field == "username") {
    return "Username";
  }
  if (school.value.integration == "infinitecampus" && field == "password") {
    return "Infinite Campus Password";
  } else if (school.value.integration != "infinitecampus" && field == "password") {
    return "Password";
  }
}

const loginState = ref("default");
const toggleValidation = ref(false);
const toggleSubmit = ref(false);
const toggleFail = ref(false);

onMounted(() => {
  if (school.value.school_id) {
    authenticationStore
      .googleUrl(school.value.district_id, school.value.school_id, "parent")
      .then((response) => {
        googleUrl.value = response;
      });
    return true;
  } else {
    if (route.params.account != null && route.params.account != "") {
      schoolStore.fetchSchool(route.params.account).then(() => {
        school.value = schoolStore.school;
        authenticationStore
          .googleUrl(school.value.district_id, school.value.school_id, "parent")
          .then((response) => {
            // console.log(response);
            googleUrl.value = response;
          });
        console.log("School found");
      });
    } else {
      window.location.href = "https://www.ptcwizard.com";
    }
  }
});
</script>


<style scoped>
.dropdown-item {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.dropdown-item.active {
  background-color: #007bff;
  color: white;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.active:hover {
  background-color: #0056b3;
}

/* RTL adjustments for login form */
[dir="rtl"] .dropdown-menu {
  right: 0;
  left: auto;
}

[dir="rtl"] .text-center {
  text-align: center !important;
}
</style>
